// WHAT: Main screen component for Impact vs Effort Matrix view
// WHY:  Provides 2x2 matrix layout for prioritizing roadmap items by impact and effort
// DOMAIN: Admin → Roadmap → Matrix
// DEPENDENCIES: useFilteredRoadmap hook, MatrixHeader, MatrixQuadrant, MatrixLegend components
// RISKS:
//   - NOTE: Medium values are assigned to closest category - may not be accurate
//   - NOTE: Items without impact_score or effort_estimate default to 'medium'
//   - NOTE: Categorization logic may place items in unexpected quadrants

import { LucideGrid, LucideTarget, LucideZap, LucideAlertTriangle } from "lucide-react";
import { useMemo } from "react";

import { Container, Stack } from "@/components/ui";

import { useFilteredRoadmap } from "../../hooks";
import { type Roadmap_Item } from "../../types/roadmap-types";
import { MatrixHeader, MatrixQuadrant, MatrixLegend } from "./components";

/**
 * WHAT:
 *   Main screen component that displays roadmap items in a 2x2 Impact vs Effort Matrix,
 *   categorizing items into four quadrants: Quick Wins, Major Projects, Fill-ins, Thankless Tasks.
 *
 * WHY:
 *   Provides a visual prioritization tool that helps users identify high-value items
 *   (high impact, low effort) and avoid low-value items (low impact, high effort).
 *
 * BEHAVIOR:
 *   - Categorizes items based on impact_score and effort_estimate
 *   - Groups items into four quadrants (high/low impact × high/low effort)
 *   - Handles medium values by assigning to closest category
 *   - Displays quadrants in responsive grid (1 column mobile, 2 columns desktop)
 *   - Shows legend explaining quadrant meanings
 *
 * RISKS:
 *   - NOTE: Medium values assignment logic may not match user expectations
 *   - NOTE: Items without impact_score/effort_estimate default to 'medium' - may skew categorization
 *   - NOTE: Categorization is binary (high/low) - no granularity for medium values
 *
 * @returns {JSX.Element} Die gerenderte MatrixScreen-Komponente
 *
 */

export const MatrixScreen = () => {
  // NOTE: Get filtered roadmap items
  const items = useFilteredRoadmap();

  // NOTE: Categorize items by impact and effort into four quadrants
  const matrixData = useMemo(() => {
    const categories = {
      "high-impact-high-effort": [] as Roadmap_Item[],
      "high-impact-low-effort": [] as Roadmap_Item[],
      "low-impact-high-effort": [] as Roadmap_Item[],
      "low-impact-low-effort": [] as Roadmap_Item[],
    };

    for (const item of items) {
      const impact = item.impact_score ?? "medium";
      const effort = item.effort_estimate ?? "medium";

      if (impact === "high" && effort === "low") {
        categories["high-impact-low-effort"].push(item);
      } else if (impact === "high" && effort === "high") {
        categories["high-impact-high-effort"].push(item);
      } else if (impact === "low" && effort === "low") {
        categories["low-impact-low-effort"].push(item);
      } else if (impact === "low" && effort === "high") {
        categories["low-impact-high-effort"].push(item);
      } else {
        // Medium values go to the closest category
        if (impact === "high" || effort === "low") {
          categories["high-impact-low-effort"].push(item);
        } else {
          categories["low-impact-high-effort"].push(item);
        }
      }
    }

    return categories;
  }, [items]);

  const quadrants = [
    {
      color: "green-500",
      description: "High Impact, Low Effort",
      icon: LucideZap,
      id: "high-impact-low-effort",
      priority: 1,
      textColor: "green-500",
      title: "Quick Wins",
    },
    {
      color: "tracq-bright-blue",
      description: "High Impact, High Effort",
      icon: LucideTarget,
      id: "high-impact-high-effort",
      priority: 2,
      textColor: "tracq-bright-blue",
      title: "Major Projects",
    },
    {
      color: "slate-500",
      description: "Low Impact, Low Effort",
      icon: LucideGrid,
      id: "low-impact-low-effort",
      priority: 3,
      textColor: "slate-400",
      title: "Fill-ins",
    },
    {
      color: "orange-500",
      description: "Low Impact, High Effort",
      icon: LucideAlertTriangle,
      id: "low-impact-high-effort",
      priority: 4,
      textColor: "orange-500",
      title: "Thankless Tasks",
    },
  ];

  return (
    <Container className="text-white" maxWidth="full" padding="lg">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <MatrixHeader />

        {/* Matrix Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {quadrants.map((quadrant) => {
            const quadrantItems = matrixData[quadrant.id as keyof typeof matrixData];

            return (
              <MatrixQuadrant
                color={quadrant.color}
                description={quadrant.description}
                icon={quadrant.icon}
                id={quadrant.id}
                items={quadrantItems}
                key={quadrant.id}
                priority={quadrant.priority}
                textColor={quadrant.textColor}
                title={quadrant.title}
              />
            );
          })}
        </div>

        {/* Matrix Legend */}
        <MatrixLegend />
      </Stack>
    </Container>
  );
};

// Named export only - no default export per project rules
